import React, { Fragment, useEffect, useRef } from "react";
import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import PaymentService from "../../Api/services/PaymentService";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

const Thankyou = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const location = useLocation();

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;

    const normalizeAmount = (value) => {
      if (value === null || value === undefined || value === "") return 0;
      const parsed = Number(String(value).replace(/,/g, "").trim());
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const normalizeTransactionId = (value) => {
      if (value === null || value === undefined) return "";
      return String(value).trim();
    };

    const normalizeEmail = (email) => {
      return email ? String(email).trim().toLowerCase() : "";
    };

    const normalizePhone = (phone) => {
      return phone ? String(phone).replace(/[^\d+]/g, "").trim() : "";
    };

    const getStoredJson = (key) => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn(`Invalid stored JSON for key: ${key}`, e);
        return null;
      }
    };

    const setEnhancedConversionData = (payload) => {
      try {
        if (typeof window.gtag !== "function") {
          console.warn("gtag not available for enhanced conversions");
          return;
        }

        const email =
          normalizeEmail(payload.email) ||
          normalizeEmail(payload.customer_email) ||
          normalizeEmail(payload.user_email);

        const phone =
          normalizePhone(payload.phone) ||
          normalizePhone(payload.mobile) ||
          normalizePhone(payload.customer_phone);

        const userData = {};
        if (email) userData.email = email;
        if (phone) userData.phone_number = phone;

        if (Object.keys(userData).length > 0) {
          window.gtag("set", "user_data", userData);
          console.log("Enhanced conversion data set", userData);
        } else {
          console.log("Enhanced conversion skipped: no email/phone");
        }
      } catch (e) {
        console.warn("Enhanced conversion setup failed", e);
      }
    };

    const pushPurchaseEvent = (payload) => {
      const transactionId = normalizeTransactionId(
        payload.transaction_id || payload.order_id
      );

      console.log("ppp", payload);

      if (!transactionId) {
        console.warn("Tracking skipped: transaction_id missing", payload);
        return;
      }

      const amount = normalizeAmount(payload.total_price ?? payload.value);

      if (amount <= 0) {
        console.warn("Tracking skipped: purchase value is 0 or invalid", payload);
        return;
      }

      const productType =
        payload.product_type ||
        localStorage.getItem("camper_order_type") ||
        "rental";

      const eventName =
        productType === "subscription"
          ? "purchase_camper_subscription"
          : productType === "giftcard"
            ? "purchase_giftcard"
            : "purchase_camper_rental";

      const storageKey = `tracked_purchase_${transactionId}`;
      if (
        window[`_tracked_${transactionId}`] ||
        localStorage.getItem(storageKey)
      ) {
        console.log("Tracking skipped: already tracked", transactionId);
        return;
      }

      window[`_tracked_${transactionId}`] = true;

      const gclidData = getStoredJson("_btc_gclid");
      const utmData = getStoredJson("_btc_utm");

      setEnhancedConversionData(payload);

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: String(eventName),
        transaction_id: String(transactionId),
        value: Number(amount),
        currency: "EUR",
        product_type: String(productType),
        camper_model: String(payload.camper_model || ""),
        start_date: String(payload.start_date || ""),
        end_date: String(payload.end_date || ""),
        payment_type: String(payload.payment_type || "cash"),
        ...(gclidData && {
          gclid: gclidData.value,
          gclid_type: gclidData.type,
        }),
        ...(utmData && {
          utm_source: utmData.source,
          utm_medium: utmData.medium,
          utm_campaign: utmData.campaign,
        }),
      });

      localStorage.setItem(storageKey, "true");

      console.log("Purchase tracked successfully", {
        eventName,
        transactionId,
        amount,
        productType,
        gclidData,
        utmData,
      });
    };

    async function trackPurchase() {
      try {
        const stripeId = searchParams.get("id");

        if (stripeId) {
          const response =
            await PaymentService.checkstripepayment(stripeId).json();

          if (!response?.order_id) {
            console.warn(
              "Tracking skipped: Stripe response missing order_id",
              response
            );
            return;
          }

          const savedRequest = JSON.parse(
            localStorage.getItem("stripedata") || "{}"
          );
          const savedDetail = JSON.parse(
            localStorage.getItem("detailData") || "{}"
          );

          pushPurchaseEvent({
            transaction_id: response.order_id,
            total_price: response.total_price ?? savedRequest.total_amount,
            camper_model: response.camper_model ?? savedDetail.title,
            start_date: response.start_date ?? savedRequest.start_date,
            end_date: response.end_date ?? savedRequest.end_date,
            payment_type: "stripe",
            email: response.email ?? savedRequest.email,
            phone: response.phone ?? savedRequest.phone,
            product_type:
              response.product_type ||
              localStorage.getItem("camper_order_type") ||
              "rental",
          });

          const userId = localStorage.getItem("userId");
          if (userId) {
            try {
              await PaymentService.registerconfirm(userId, {
                order_status: 1,
              }).json();
            } catch (confirmError) {
              console.warn("registerconfirm failed", confirmError);
            }
          }

          return;
        }

        const state = location.state || {};
        let cashPayload = null;

        if (state?.order_id) {
          cashPayload = {
            transaction_id: state.order_id,
            total_price: state.total_price,
            camper_model: state.camper_model,
            start_date: state.start_date,
            end_date: state.end_date,
            payment_type: state.payment_type || "cash",
            email: state.email,
            phone: state.phone,
            product_type:
              state.product_type ||
              localStorage.getItem("camper_order_type") ||
              "rental",
          };
        }

        if (cashPayload?.transaction_id) {
          const saved = localStorage.getItem(
            `thankyou_track_${cashPayload.transaction_id}`
          );

          if (saved) {
            try {
              cashPayload = {
                ...cashPayload,
                ...JSON.parse(saved),
              };
            } catch (e) {
              console.warn("Invalid thankyou_track payload in localStorage", e);
            }
          }

          pushPurchaseEvent(cashPayload);
        }
      } catch (error) {
        console.error("Purchase tracking failed", error);
      } finally {
        localStorage.removeItem("payment_status");
        localStorage.removeItem("stripedata");
        localStorage.removeItem("stripesession");

        Cookies.set("Token", "value", { expires: 7 });
      }
    }

    trackPurchase();
  }, [location.state, searchParams]);

  return (
    <Fragment>
      <Header />
      <div className="min-h-screen bg-off-white">

        <div className="pb-5 text-center">
          <div className="overflow-hidden">
            <div className="-ml-[5%] w-[110%] overflow-hidden rounded-b-[60%20%] px-[5%]">
              <div className="relative">
                <div className="overflow-hidden">
                  <img
                    src="../Images/Thank you pages.jpg"
                    alt="hero-banner"
                    className="absolute max-h-full w-full scale-110 object-cover blur-lg dark:brightness-90 sm:aspect-hero-regular 3xl:aspect-hero-long aspect-square"
                  />

                </div>

                <picture className="flex max-h-[65vh] w-full text-0">

                  <img
                    className="z-0 max-h-full w-full object-cover dark:brightness-90 sm:aspect-hero-regular 3xl:aspect-hero-long aspect-square"
                    src="../Images/Thank you pages.jpg"
                    alt="hero-banner"
                    width="2552"
                    height="1286"
                  />

                </picture>

                <div className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-end">
                  <div className="pb-12 2xl:pb-14 w-full bg-gradient-to-t from-black-pure/80 to-transparent pt-20 md:pt-14 xl:pt-32">
                    <h1 className="whitespace-pre-line break-words m-auto max-w-4xl px-8 text-xl md:text-2xl text-white drop-shadow-text dark:text-off-white xs:text-2xl xl:text-3xl mb-5 md:mb-8 xl:mb-12">
                      {t("thankyou.Thank You")}
                    </h1>

                  </div>

                </div>

              </div>

            </div>
          </div>
        </div>
        <div className="mx-5 max-w-screen-xl xl-padded:mx-auto">
          <div className="xl:pt-8 pt-3 2xl:pt-8 pb-8 2xl:pb-12 mx-auto flex max-w-4xl flex-col items-center text-center">

            <div className="inline-block align-top dark:cssvar-white-to-off-white dark:cssvar-secondary-to-secondary dark:cssvar-primary-to-primary-dark mb-4 child:h-20 child:w-20">

              <img
                src="../Images/img-aboutus-header-banner.svg"
                alt="aboutus-header-banner"
              />

            </div>

            <h2 className="whitespace-pre-line break-words text-xl md:text-2xl mb-4">
              {t("thankyou.Thank you for your trust!")}
            </h2>

            <p className="mb-4 whitespace-pre-line text-2md font-normal md:text-lg-paragraph md:font-normal text-20">

              {t(
                "thankyou.You will soon receive a confirmation email with your login password for our website.",
              )}{" "}

              {t(
                "thankyou.There, you can manage your booking, ask questions, view instructions, and much more!",
              )}

            </p>

            <p className="mb-4 whitespace-pre-line text-2md font-normal md:text-lg-paragraph md:font-normal text-20 bold-strong">

              <strong style={{ textDecoration: "underline" }}>
                {t("thankyou.Important")}

              </strong>
              :
              {t(
                "thankyou.Take a moment to check the Login section on our Website before your trip – it’ll make your start easier and your adventure even more relaxed!",
              )}

            </p>

            <p className="mb-2 whitespace-pre-line text-2md font-normal md:text-lg-paragraph md:font-normal text-20">

              {t("thankyou.We look forward to meeting you upon pickup!")}

            </p>

            <p className="mb-2 whitespace-pre-line text-2md font-normal md:text-lg-paragraph md:font-normal text-20">

              {t("thankyou.Do you have any questions? Just reach out to us!")}

            </p>

            <p className="mb-2 whitespace-pre-line text-2md font-normal md:text-lg-paragraph md:font-normal text-20">
              WhatsApp: +49 163 2937354
            </p>

            <p className="mb-2 whitespace-pre-line text-2md font-normal md:text-lg-paragraph md:font-normal text-20">
              {t("thankyou.Go To")}

              <Link
                className="w-full text-center hover:text-primary hover:dark:text-primary-light mb-2 last:mb-0"
                to="/"
              >
                {t("thankyou.Home")}

              </Link>

            </p>

          </div>

        </div>

      </div>

      <Footer />

    </Fragment>
  );
};

export default Thankyou;